import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { useSessionManager } from "../../apiHooks/useCoachAISessionManager";
import { useStoreCoachBotsGet } from "../../stores/useStoreCoachBotsGet";
import Image from "../Tags/Image";
import VoiceAssistant from "./VoiceAssistant";
import { coachAIGenerateCallReport } from "../../apis/callLogs";
import { useMutation } from "react-query";
import { useStoreCoachAi } from "../../stores/useStoreCoachAi";
import Text from "../Tags/Text";
import { useCaptureEvent } from "../../hooks/useCaptureEvent";
import toast from "react-hot-toast";

const getSpeakerLabel = (role) => {
  if (role === "client") return "You";
  if (role === "server") return "CoachAI";
  return "";
};

const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";

  try {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch (err) {
    console.error("Failed to format timestamp", err);
    return "";
  }
};

const generateCoachAIUUID = () => {
  const prefix = "COACH-AI";
  const timestamp = Date.now(); // current timestamp in milliseconds

  // generate 4 random uppercase letters
  const randomLetters = Array.from({ length: 4 }, () =>
    String.fromCharCode(65 + Math.floor(Math.random() * 26))
  ).join("");

  return `${prefix}-${timestamp}-${randomLetters}`;
};

// const formatDuration = (ms) => {
//   if (!ms || ms <= 0) return "0:00";

//   const totalSeconds = Math.floor(ms / 1000);
//   const minutes = Math.floor(totalSeconds / 60);
//   const seconds = totalSeconds % 60;
//   return `${minutes}:${seconds.toString().padStart(2, "0")}`;
// };

export default function CallingAgent({ activeWorkspace, viewReport }) {
  const { setReportData, setTranscriptData } = useStoreCoachAi();
  const { isSessionActive, startSession, stopSession, clientMessages, serverMessages, error, events } =
    useSessionManager();
  const captureEvent = useCaptureEvent();

  const [isStartingSession, setIsStartingSession] = useState(false);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [completedTranscript, setCompletedTranscript] = useState([]);
  const transcriptContainerRef = useRef(null);
  const previousIsSessionActiveRef = useRef(isSessionActive);
  const { activeBotId, coachBots, setCallInProgress } = useStoreCoachBotsGet();
  const [voiceAssistantState, setVoiceAssistantState] = useState("idle");
  const [isClientSpeaking, setIsClientSpeaking] = useState(false);
  const [isServerSpeaking, setIsServerSpeaking] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionDuration, setSessionDuration] = useState(0);

  const transcript = useMemo(() => {
    const combined = [...clientMessages, ...serverMessages];

    return combined.sort((a, b) => a.timestamp - b.timestamp);
  }, [clientMessages, serverMessages]);

  const activeBot = useMemo(
    () => coachBots?.find((bot) => bot?.id === activeBotId) ?? null,
    [coachBots, activeBotId]
  );

  const backgroundItems = useMemo(() => {
    const background = activeBot?.background ?? "";

    return background
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }, [activeBot?.background]);

  const callInProgress = isSessionActive || isStartingSession;
  const displayName = activeBot
    ? `${activeBot.first_name || ""} ${activeBot.last_name || ""}`.trim() || "CoachAI"
    : "CoachAI";
  const displayRole = activeBot?.role || "AI Expert";

  useEffect(() => {
    setSessionEnded(false);
    setCallInProgress(false);
    setSessionStartTime(null);
    setSessionDuration(0);
  }, [activeBotId]);

  const renderCallTypeBadge = () => {
    const callType = activeBot?.call_type;
    if (!callType) return null;

    if (callType === "cold") {
      return (
        <Badge radius="sm" variant="gray">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clipPath="url(#clip0_call_type_cold)">
              <path
                d="M9.02918 4.25L2.96701 7.75M9.02918 4.25L9.57822 2.20096M9.02918 4.25L11.0782 4.79904M2.96701 7.75L0.917969 7.20096M2.96701 7.75L2.41797 9.79904M9.02914 7.74993L2.96696 4.24993M9.02914 7.74993L11.0782 7.2009M9.02914 7.74993L9.57823 9.79898M2.96696 4.24993L2.41804 2.20102M2.96696 4.24993L0.91804 4.79909M5.9981 2.5L5.9981 9.5M5.9981 2.5L4.4981 1M5.9981 2.5L7.4981 1M5.9981 9.5L4.4981 11M5.9981 9.5L7.4981 11"
                stroke="#535862"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_call_type_cold">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Cold call
        </Badge>
      );
    }

    if (callType === "discovery") {
      return (
        <Badge radius="sm" variant="gray">
          <svg
            width="12"
            height="12"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="mr-1"
          >
            <g clipPath="url(#clip0_call_type_discovery)">
              <path
                d="M10.5 10.5L8.325 8.325M9.5 5.5C9.5 7.70914 7.70914 9.5 5.5 9.5C3.29086 9.5 1.5 7.70914 1.5 5.5C1.5 3.29086 3.29086 1.5 5.5 1.5C7.70914 1.5 9.5 3.29086 9.5 5.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </g>
            <defs>
              <clipPath id="clip0_call_type_discovery">
                <rect width="12" height="12" fill="white" />
              </clipPath>
            </defs>
          </svg>
          Discovery call
        </Badge>
      );
    }

    return null;
  };

  const renderToneBadge = () => {
    const tone = activeBot?.tone;
    if (!tone) return null;

    const commonIcon = (
      <svg
        width="12"
        height="12"
        viewBox="0 0 12 12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="mr-1"
      >
        <path
          d="M7.5 4.5H7.505M4.5 4.5H4.505M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6ZM7.75 4.5C7.75 4.63807 7.63807 4.75 7.5 4.75C7.36193 4.75 7.25 4.63807 7.25 4.5C7.25 4.36193 7.36193 4.25 7.5 4.25C7.63807 4.25 7.75 4.36193 7.75 4.5ZM4.75 4.5C4.75 4.63807 4.63807 4.75 4.5 4.75C4.36193 4.75 4.25 4.63807 4.25 4.5C4.25 4.36193 4.36193 4.25 4.5 4.25C4.63807 4.25 4.75 4.36193 4.75 4.5ZM6 8.75C7.25025 8.75 8.25 7.8335 8.25 7H3.75C3.75 7.8335 4.74975 8.75 6 8.75Z"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );

    if (tone === "rude") {
      return (
        <Badge variant="error-outline">
          {commonIcon}
          Rude
        </Badge>
      );
    }

    if (tone === "cheerful") {
      return (
        <Badge variant="blue-outline">
          {commonIcon}
          Cheerful
        </Badge>
      );
    }

    if (tone === "neutral") {
      return (
        <Badge variant="gray-outline">
          {commonIcon}
          Neutral
        </Badge>
      );
    }

    return null;
  };

  const getBotInitials = () => {
    if (!activeBot) return "CA";
    const first = activeBot.first_name?.[0] || "";
    const last = activeBot.last_name?.[0] || "";
    const initials = `${first}${last}`.trim();
    return initials || "CA";
  };

  const handleStartSession = async () => {
    if (callInProgress) return;
    if (!activeBotId) {
      console.warn("No CoachAI bot selected; cannot start session.");
      return;
    }

    setSessionEnded(false);
    setCompletedTranscript([]);
    setIsStartingSession(true);
    setSessionStartTime(Date.now());
    setSessionDuration(0);
    try {
      captureEvent("CAI_StartCallClick", { bot_id: activeBotId });
      await startSession({ botId: activeBotId });
    } catch (err) {
      console.error("Failed to start CoachAI session:", err);
      captureEvent("CAI_StartCall_Failure", { bot_id: activeBotId, error: String(err?.message || err) });
      setIsStartingSession(false);
      setSessionStartTime(null);
      setSessionDuration(0);
    }
  };

  const handleStopSession = () => {
    // final session duration
    if (sessionStartTime) {
      const finalDuration = Date.now() - sessionStartTime;
      setSessionDuration(finalDuration);
    }

    if (transcript.length > 0) {
      setCompletedTranscript(transcript);
      handleCoachAIGenerateCallReport();
      setTranscriptData(transcript);
    } else {
      setTranscriptData(null);
    }
    setSessionEnded(true);
    captureEvent("CAI_StopCallClick", {
      bot_id: activeBotId,
      had_transcript: transcript.length > 0,
      session_duration_ms: sessionStartTime ? Date.now() - sessionStartTime : 0,
    });
    stopSession();
    setIsStartingSession(false);
  };

  useEffect(() => {
    if (isSessionActive) {
      setIsStartingSession(false);
    }
  }, [isSessionActive]);

  useEffect(() => {
    if (error) {
      setIsStartingSession(false);
    }
  }, [error]);

  useEffect(() => {
    if (!transcriptContainerRef.current) return;
    const contentToScroll = sessionEnded ? completedTranscript : transcript;
    if (!contentToScroll.length) return;
    transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
  }, [sessionEnded, transcript, completedTranscript]);

  useEffect(() => {
    const wasActive = previousIsSessionActiveRef.current;

    if (!wasActive && isSessionActive) {
      setSessionEnded(false);
    }

    if (wasActive && !isSessionActive) {
      if (transcript.length > 0) {
        setCompletedTranscript(transcript);
      }
      setSessionEnded(true);
    }

    previousIsSessionActiveRef.current = isSessionActive;
  }, [isSessionActive, transcript]);

  useEffect(() => {
    setCallInProgress(callInProgress);
  }, [callInProgress, setCallInProgress]);

  useEffect(() => {
    if (!isSessionActive || !sessionStartTime) {
      return;
    }

    const interval = setInterval(() => {
      setSessionDuration(Date.now() - sessionStartTime);
    }, 1000);

    return () => clearInterval(interval);
  }, [isSessionActive, sessionStartTime]);

  const markClientSpeaking = useCallback(() => {
    setIsClientSpeaking(true);
  }, []);

  const markClientSilent = useCallback(() => {
    setIsClientSpeaking(false);
  }, []);

  const markServerSpeaking = useCallback(() => {
    setIsServerSpeaking(true);
  }, []);

  const markServerSilent = useCallback(() => {
    setIsServerSpeaking(false);
  }, []);

  const resetVoiceActivity = useCallback(() => {
    setIsClientSpeaking(false);
    setIsServerSpeaking(false);
    setVoiceAssistantState("idle");
  }, []);

  useEffect(() => {
    if (error) {
      resetVoiceActivity();
    }
  }, [error, resetVoiceActivity]);

  useEffect(() => {
    if (!callInProgress) {
      resetVoiceActivity();
    }
  }, [callInProgress, resetVoiceActivity]);

  useEffect(() => {
    if (!callInProgress || !events?.length) return;

    const latestEvent = events[0];
    const eventType = latestEvent?.type;
    if (!eventType) return;

    if (eventType.startsWith("input_audio_buffer.")) {
      if (
        eventType === "input_audio_buffer.speech_stopped" ||
        eventType === "input_audio_buffer.stopped" ||
        eventType === "input_audio_buffer.cleared"
      ) {
        markClientSilent();
      } else {
        markClientSpeaking();
      }
    }

    if (eventType === "conversation.item.input_audio_transcription.partial") {
      markClientSpeaking();
    }

    if (eventType === "conversation.item.input_audio_transcription.completed") {
      markClientSilent();
    }

    console.log("eventType", eventType);

    if (
      eventType.startsWith("response.output_audio.") ||
      eventType.startsWith("response.audio.") ||
      eventType.startsWith("response.audio_transcript.") ||
      eventType.startsWith("response.output_item.") ||
      eventType === "response.created" ||
      eventType === "response.started"
    ) {
      markServerSpeaking();
    }

    if (
      eventType === "response.completed" ||
      eventType === "response.canceled" ||
      eventType === "output_audio_buffer.stopped"
    ) {
      markServerSilent();
    }
  }, [events, callInProgress, markClientSpeaking, markClientSilent, markServerSpeaking, markServerSilent]);

  useEffect(() => {
    if (!callInProgress || !serverMessages.length) return;

    markServerSpeaking();
  }, [serverMessages, callInProgress, markServerSpeaking]);

  useEffect(() => {
    if (!callInProgress || !isSessionActive || error) {
      setVoiceAssistantState("idle");
      return;
    }

    if (isClientSpeaking) {
      setVoiceAssistantState("mic");
      return;
    }

    if (isServerSpeaking) {
      setVoiceAssistantState("speaker");
      return;
    }

    setVoiceAssistantState("idle");
  }, [callInProgress, isSessionActive, error, isClientSpeaking, isServerSpeaking]);

  const coachAIGenerateCallReportFn = useMutation(
    "generate_call_report_coach_ai",
    coachAIGenerateCallReport,
    {
      onSuccess(data, variables, context) {
        setReportData(data?.data);
        toast.success(data?.message);
        captureEvent("CAI_GenerateReport_Success", { bot_id: activeBotId });
      },
      onError(error) {
        captureEvent("CAI_GenerateReport_Failure", {
          bot_id: activeBotId,
          error: error?.message || "unknown_error",
        });
      },
    }
  );

  const handleCoachAIGenerateCallReport = () => {
    coachAIGenerateCallReportFn.mutate({
      data: {
        bot_id: activeBotId,
        transcript: transcript,
        session_id: generateCoachAIUUID(),
        duration: Math.floor(sessionDuration / 1000),
      },
      options: {
        headers: { "X-Workspace-Id": activeWorkspace?.id },
      },
    });
  };

  if (!activeBot) {
    return (
      <div className="">
        <div className="w-[500px]">
          <Text size="h6">Create a bot to get started</Text>
          <Text className="mt-5">
            Practice your pitch, sharpen your messaging, and build confidence through dynamic, lifelike
            interactions.
          </Text>
        </div>
        <div className="text-center">
          <Image
            src="/images/icons/untitledui/no-coach-bots.svg"
            alt="Img"
            className="no-invert mt-24 inline-block"
          />
        </div>
        <div className="grid grid-cols-2 gap-8 mt-16 max-w-4xl mx-auto">
          <div className="text-center border-t-2 border-[#E9D7FE] pt-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Master tough conversations</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Practice with AI prospects who react like real buyers—skeptical, busy, and guarded. Build the
              muscle memory to handle objections naturally.
            </p>
          </div>
          <div className="text-center border-t-2 border-[#E9D7FE] pt-4">
            <h3 className="text-base font-semibold text-gray-900 mb-2">
              Get prospects anytime, without scheduling
            </h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              No need to wait for role-play partners or manager availability. Jump into realistic practice
              scenarios whenever you need them.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        className={`border p-3 rounded-xl bg-background gap-3 min-h-[88px] relative ${callInProgress && "chat__agent__background"}`}
      >
        {callInProgress && (
          <div className="mb-3 flex items-center justify-between">
            <Badge radius="sm" variant="gray">
              <Badge size="dot" variant="error-dot" className="mr-1" />
              {isSessionActive ? "Call in progress" : "Connecting"}
            </Badge>
            {/* {isSessionActive && sessionStartTime && (
              <Badge radius="sm" variant="gray">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mr-1"
                >
                  <path
                    d="M6 3V6L8 7M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {formatDuration(sessionDuration)}
              </Badge>
            )} */}
          </div>
        )}
        <div className="flex items-center space-x-3 w-full">
          <div className="relative h-12 w-12 flex items-center justify-center">
            {callInProgress && (
              <>
                <span className="absolute inset-0 rounded-full bg-brand-200 opacity-70 animate-ping" />
                <span className="absolute inset-1 rounded-full bg-brand-100 opacity-80 animate-pulse" />
              </>
            )}
            {/* <span className="relative z-10 inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-400 bg-brand-100 text-sm font-semibold text-brand-900">
              {getBotInitials()}
            </span> */}
            {activeBot?.avatar &&
            !activeBot?.avatar.includes("https://") &&
            !activeBot?.avatar.includes(".jpg") ? (
              <Image
                src={`/images/bots/${activeBot?.avatar && activeBot?.avatar.includes(".svg") ? activeBot?.avatar : `${activeBot?.avatar}.svg`}`}
                width={40}
                height={40}
                className="rounded w-10 h-10 no-invert"
              />
            ) : (
              <Image src={`/images/bots/avatar_1.svg`} width={40} height={40} className="rounded w-10 h-10" />
            )}
          </div>
          <div className="inline-flex justify-between items-center flex-1">
            <span>
              <span className="text-[14px] text-gray-950 block font-bold">{displayName}</span>
              <span className="text-[12px] text-900 block mt-0.5">{displayRole}</span>
            </span>
            <div className="inline-flex space-x-2">
              {renderCallTypeBadge()}
              {renderToneBadge()}
            </div>
          </div>
        </div>
        {callInProgress ? (
          <div className="mt-10 space-y-6">
            <div className=" rounded-md p-4">
              <div className="flex flex-col items-center justify-center py-12">
                {isStartingSession && <span className="text-white font-bold">Linking to persona...</span>}

                {isSessionActive && <VoiceAssistant state={voiceAssistantState} className="max-w-xs" />}
              </div>
            </div>

            <div className="flex items-center justify-center">
              <Button className="mt-1 px-24 rounded-full mb-3" onClick={handleStopSession} variant="danger">
                <svg
                  width="27"
                  height="27"
                  viewBox="0 0 27 27"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#clip0_13070_17995)">
                    <path
                      d="M8.55765 12.7051C10.1832 12.1342 11.9313 11.8237 13.752 11.8237C15.5727 11.8237 17.3208 12.1342 18.9464 12.7051C19.0862 12.7543 19.1561 12.7788 19.2333 12.8212C19.5076 12.9716 19.724 13.2769 19.775 13.5856C19.7894 13.6725 19.7894 13.7591 19.7894 13.9323C19.7894 14.462 19.7894 14.7269 19.836 14.9466C20.0117 15.7754 20.6591 16.4228 21.4879 16.5985C21.7076 16.6451 21.9725 16.6451 22.5022 16.6451L22.7975 16.6451C23.6027 16.6451 24.0054 16.6451 24.3309 16.5383C24.9784 16.3257 25.4862 15.8179 25.6987 15.1705C25.8056 14.8449 25.8056 14.4423 25.8056 13.637L25.8056 13.3982C25.8056 12.5957 25.8056 12.1944 25.686 11.7684C25.5534 11.2957 25.2056 10.7034 24.8574 10.3573C24.5436 10.0453 24.2691 9.89221 23.7201 9.58595C20.7696 7.94009 17.3703 7.00225 13.752 7.00225C10.1337 7.00225 6.73438 7.94009 3.78396 9.58595C3.23495 9.89221 2.96044 10.0453 2.64666 10.3573C2.29847 10.7034 1.95063 11.2957 1.81798 11.7684C1.69844 12.1944 1.69844 12.5957 1.69844 13.3982V13.637C1.69844 14.4423 1.69844 14.8449 1.80529 15.1705C2.0178 15.8179 2.5256 16.3258 3.17308 16.5383C3.49865 16.6451 3.90127 16.6451 4.70653 16.6451H5.0018C5.53154 16.6451 5.7964 16.6451 6.01616 16.5985C6.8449 16.4228 7.49235 15.7754 7.66803 14.9466C7.71462 14.7269 7.71462 14.462 7.71462 13.9323C7.71462 13.7591 7.71462 13.6725 7.72899 13.5856C7.78007 13.2769 7.99641 12.9716 8.27073 12.8212C8.34792 12.7788 8.41783 12.7543 8.55765 12.7051Z"
                      stroke="white"
                      strokeWidth="2.14286"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </g>
                  <defs>
                    <clipPath id="clip0_13070_17995">
                      <rect
                        width="25.7143"
                        height="25.7143"
                        fill="white"
                        transform="translate(0.644531 0.642578)"
                      />
                    </clipPath>
                  </defs>
                </svg>
              </Button>
            </div>
          </div>
        ) : sessionEnded ? (
          <div className="mt-10 space-y-6">
            <div className="bg-gray-100 rounded-md p-4">
              <div className="mb-6 flex items-center justify-between">
                <span className="font-medium">Transcript</span>
                {/* {sessionDuration > 0 && (
                  <Badge radius="sm" variant="gray">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-1"
                    >
                      <path
                        d="M6 3V6L8 7M11 6C11 8.76142 8.76142 11 6 11C3.23858 11 1 8.76142 1 6C1 3.23858 3.23858 1 6 1C8.76142 1 11 3.23858 11 6Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Duration: {formatDuration(sessionDuration)}
                  </Badge>
                )} */}
              </div>
              {!completedTranscript.length ? (
                <div className="text-xs text-gray-500">No transcription is available for this session.</div>
              ) : (
                <div
                  ref={transcriptContainerRef}
                  className="chat__box__area space-y-4 max-h-[500px] overflow-y-auto"
                >
                  {completedTranscript.map((entry) => (
                    <div
                      key={`${entry.role}-${entry.timestamp}-${entry.text}`}
                      className={entry.role === "client" ? "flex justify-end" : "flex"}
                    >
                      <div className="max-w-[70%]">
                        <div className="text-xs mb-1 text-gray-500">{getSpeakerLabel(entry.role)}</div>
                        <div className="bg-background border border-border p-3 rounded-md inline-block whitespace-pre-line">
                          {entry.text}
                        </div>
                        <div
                          className={`text-[10px] text-gray-400 mt-1 ${
                            entry.role === "client" ? "text-right" : "text-left"
                          }`}
                        >
                          {formatTimestamp(entry.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-center mt-1 mb-3 space-x-3">
              <Button
                variant="purple_outline"
                disabled={coachAIGenerateCallReportFn.isLoading}
                className="px-12 rounded-full"
                onClick={viewReport}
              >
                {coachAIGenerateCallReportFn.isLoading ? (
                  <>
                    Generating report <div className="loader_circle ml-2"></div>
                  </>
                ) : (
                  "View report"
                )}
              </Button>
              <Button
                className="px-12 rounded-full"
                onClick={handleStartSession}
                disabled={isStartingSession || isSessionActive || !activeBotId}
              >
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="mr-2"
                >
                  <path
                    d="M18.001 7.16667V3M18.001 3H13.8343M18.001 3L13.001 8M9.0235 12.0526C8.02219 11.0512 7.23153 9.91904 6.65153 8.71102C6.60164 8.60712 6.5767 8.55516 6.55754 8.48942C6.48943 8.25579 6.53835 7.96891 6.68003 7.77105C6.71989 7.71537 6.76752 7.66774 6.86278 7.57248C7.15412 7.28115 7.29979 7.13548 7.39503 6.98899C7.75419 6.43659 7.75419 5.72443 7.39503 5.17203C7.29979 5.02554 7.15412 4.87988 6.86278 4.58854L6.70039 4.42615C6.25752 3.98328 6.03609 3.76184 5.79827 3.64156C5.32531 3.40233 4.76675 3.40233 4.29378 3.64156C4.05596 3.76184 3.83453 3.98328 3.39166 4.42615L3.2603 4.55751C2.81895 4.99886 2.59827 5.21954 2.42973 5.51956C2.24271 5.85249 2.10825 6.36956 2.10938 6.75141C2.11041 7.09554 2.17716 7.33072 2.31067 7.80109C3.02814 10.3289 4.38187 12.7142 6.37185 14.7042C8.36184 16.6942 10.7471 18.0479 13.275 18.7654C13.7453 18.8989 13.9805 18.9657 14.3246 18.9667C14.7065 18.9678 15.2236 18.8333 15.5565 18.6463C15.8565 18.4778 16.0772 18.2571 16.5186 17.8158L16.6499 17.6844C17.0928 17.2415 17.3142 17.0201 17.4345 16.7823C17.6737 16.3093 17.6737 15.7508 17.4345 15.2778C17.3142 15.04 17.0928 14.8185 16.6499 14.3757L16.4875 14.2133C16.1962 13.9219 16.0505 13.7763 15.904 13.681C15.3516 13.3219 14.6395 13.3219 14.0871 13.681C13.9406 13.7763 13.7949 13.9219 13.5036 14.2133C13.4083 14.3085 13.3607 14.3562 13.305 14.396C13.1072 14.5377 12.8203 14.5866 12.5866 14.5185C12.5209 14.4994 12.4689 14.4744 12.365 14.4245C11.157 13.8445 10.0248 13.0539 9.0235 12.0526Z"
                    stroke="white"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {isStartingSession ? "Connecting" : "Call Again"}
              </Button>
            </div>
          </div>
        ) : (
          <div>
            <div className="pl-[54px] mt-4">
              {activeBot && !activeBot?.background && (
                <div className="flex justify-center items-center flex-col my-4">
                  <Image
                    src="/images/icons/untitledui/no-background-data.svg"
                    alt="Img"
                    className="no-invert"
                  />
                  <p className="text-base text-gray-900 font-semibold mt-8">
                    No background details available
                  </p>
                </div>
              )}
              {activeBot && activeBot?.background && (
                <>
                  <span className="text-gray-950 text-sm font-bold">Background</span>
                  <ul className="list-disc pl-4 mt-5 space-y-1.5">
                    {backgroundItems.map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            <Button
              className="w-full mt-4"
              onClick={handleStartSession}
              disabled={isStartingSession || isSessionActive || !activeBotId}
            >
              <svg
                width="21"
                height="21"
                viewBox="0 0 21 21"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="mr-2"
              >
                <path
                  d="M18.001 7.16667V3M18.001 3H13.8343M18.001 3L13.001 8M9.0235 12.0526C8.02219 11.0512 7.23153 9.91904 6.65153 8.71102C6.60164 8.60712 6.5767 8.55516 6.55754 8.48942C6.48943 8.25579 6.53835 7.96891 6.68003 7.77105C6.71989 7.71537 6.76752 7.66774 6.86278 7.57248C7.15412 7.28115 7.29979 7.13548 7.39503 6.98899C7.75419 6.43659 7.75419 5.72443 7.39503 5.17203C7.29979 5.02554 7.15412 4.87988 6.86278 4.58854L6.70039 4.42615C6.25752 3.98328 6.03609 3.76184 5.79827 3.64156C5.32531 3.40233 4.76675 3.40233 4.29378 3.64156C4.05596 3.76184 3.83453 3.98328 3.39166 4.42615L3.2603 4.55751C2.81895 4.99886 2.59827 5.21954 2.42973 5.51956C2.24271 5.85249 2.10825 6.36956 2.10938 6.75141C2.11041 7.09554 2.17716 7.33072 2.31067 7.80109C3.02814 10.3289 4.38187 12.7142 6.37185 14.7042C8.36184 16.6942 10.7471 18.0479 13.275 18.7654C13.7453 18.8989 13.9805 18.9657 14.3246 18.9667C14.7065 18.9678 15.2236 18.8333 15.5565 18.6463C15.8565 18.4778 16.0772 18.2571 16.5186 17.8158L16.6499 17.6844C17.0928 17.2415 17.3142 17.0201 17.4345 16.7823C17.6737 16.3093 17.6737 15.7508 17.4345 15.2778C17.3142 15.04 17.0928 14.8185 16.6499 14.3757L16.4875 14.2133C16.1962 13.9219 16.0505 13.7763 15.904 13.681C15.3516 13.3219 14.6395 13.3219 14.0871 13.681C13.9406 13.7763 13.7949 13.9219 13.5036 14.2133C13.4083 14.3085 13.3607 14.3562 13.305 14.396C13.1072 14.5377 12.8203 14.5866 12.5866 14.5185C12.5209 14.4994 12.4689 14.4744 12.365 14.4245C11.157 13.8445 10.0248 13.0539 9.0235 12.0526Z"
                  stroke="white"
                  strokeWidth="1.66667"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {isStartingSession ? "Connecting" : "Start Call"}
            </Button>
          </div>
        )}
        {error && <div className="text-sm text-red-500 text-center mt-4">{error}</div>}
      </div>
    </div>
  );
}
